import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Play, Pause, Volume2 } from "lucide-react";

interface VoiceMessageProps {
  audioUrl: string;
  duration?: number;
  transcription?: string;
  isOwn?: boolean;
}

const VoiceMessage = ({ audioUrl, duration, transcription, isOwn = false }: VoiceMessageProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(duration || 0);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setTotalDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const formatTime = (time: number) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;

  return (
    <div className={`
      flex items-center space-x-3 p-3 rounded-lg min-w-[200px] max-w-[280px]
      ${isOwn 
        ? 'bg-electric-violet/20 border border-electric-violet/30' 
        : 'bg-ice-white/10 border border-ice-white/20'
      }
    `}>
      {/* Play/Pause Button */}
      <Button
        onClick={togglePlayback}
        className={`
          h-10 w-10 rounded-full flex-shrink-0
          ${isOwn 
            ? 'bg-electric-violet hover:bg-electric-violet/80' 
            : 'bg-neon-red hover:bg-neon-red/80'
          }
        `}
      >
        {isPlaying ? (
          <Pause className="w-5 h-5 text-ice-white" />
        ) : (
          <Play className="w-5 h-5 text-ice-white" />
        )}
      </Button>

      {/* Waveform/Progress Area */}
      <div className="flex-1 space-y-2">
        {/* Waveform visualization (simplified as progress bar) */}
        <div className="flex items-center space-x-2">
          <Volume2 className="w-4 h-4 text-ice-white/60" />
          <div className="flex-1 h-2 bg-ice-white/20 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-100 ${
                isOwn ? 'bg-electric-violet' : 'bg-neon-red'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Time Display */}
        <div className="flex justify-between text-xs text-ice-white/60">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(totalDuration)}</span>
        </div>

        {/* Transcription */}
        {transcription && (
          <div className="text-xs text-ice-white/80 bg-carbon/30 rounded px-2 py-1 mt-2">
            "{transcription}"
          </div>
        )}
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
    </div>
  );
};

export default VoiceMessage;